/**
 * Owns the lifecycle of the stores scoped to a signed-in session. Sign-in, sign-out,
 * session expiry and account switching all show up as a change of the signed-in user id,
 * which is the single trigger for loading and resetting those stores.
 */
import {useAuthStore} from '@src/store/auth_store';
import {useBoardStore} from '@src/store/board_store';
import {useBoardTreeStore} from '@src/store/board_tree_store';
import {useDemoBoardsStore} from '@src/store/demo_data/demo_boards_store';
import {useOrgStore} from '@src/store/org_store';
import {useUiStore} from '@src/store/ui_store';
import {useUserStore} from '@src/store/user_store';
import {watch} from 'vue';

/**
 * Load the session-scoped stores for the current signed-in user, then watch for changes.
 * Resolves once the initial loads have settled; the watchers only react to later changes.
 * Call once from the root component.
 */
export async function startSessionScopedStores(): Promise<void> {
   const auth_store = useAuthStore();
   const board_store = useBoardStore();
   const board_tree_store = useBoardTreeStore();
   const demo_boards_store = useDemoBoardsStore();
   const orgs_store = useOrgStore();
   const ui_store = useUiStore();
   const user_store = useUserStore();

   const loadForSignedInUser = async (): Promise<void> => {
      await Promise.all([user_store.ensureLoaded(), orgs_store.ensureLoaded()]);
   };

   const syncDemoContextData = (): void => {
      demo_boards_store.ensureWorkspaceContext({
         orgId: orgs_store.activeOrg?.id ?? null,
         orgRole: orgs_store.myRole,
         user: auth_store.user,
      });
   };

   // The open board never survives an organization change; the tree reloads for the new one.
   let synced_org_id: string | null = null;
   const syncBoardTree = (): void => {
      const org_id = orgs_store.activeOrg?.id ?? null;
      if (synced_org_id != null && org_id !== synced_org_id) {
         board_store.clear();
      }
      synced_org_id = org_id;
      void board_tree_store.ensureLoadedForOrg();
   };

   await loadForSignedInUser();
   syncDemoContextData();
   syncBoardTree();

   // Both stores reset themselves when there is no signed-in user.
   watch(
      () => auth_store.signedInUserId,
      () => {
         void loadForSignedInUser();
      },
   );

   // The details row carries the user's UI preferences; the server copy wins on load.
   watch(
      () => user_store.details,
      (details) => {
         if (details != null) {
            ui_store.applyServerPreferences(details);
         }
      },
      {immediate: true},
   );

   // A change to the active organization, the caller's role, or the signed-in user is the
   // same trigger for both the boards demo store and the real board tree: what we know
   // about the boards is scoped to those, so refresh both together.
   watch(
      [
         () => orgs_store.activeOrg?.id ?? null,
         () => orgs_store.myRole,
         () => auth_store.signedInUserId,
      ],
      () => {
         syncDemoContextData();
         syncBoardTree();
      },
   );
}
